import React from "react";
import { Checkbox, TextField } from "@mui/material";
import {
  QuestionConfigAnswerOptionDto,
  QuestionConfigDetailDto,
} from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { generateId } from "@helpers";
import { Button, Stack, IconButton } from "@components/base";
import {
  emptyAnswerOptionTranslations,
  useQuestionEditorStore,
} from "@components/main/questions/QuestionEditor";

interface CheckboxInput {
  questionConfig: QuestionConfigDetailDto;
  setQuestionConfig: (questionConfig: QuestionConfigDetailDto) => void;
}

const CheckboxInput: React.FC<CheckboxInput> = ({
  questionConfig,
  setQuestionConfig,
}) => {
  const { language } = useQuestionEditorStore();
  const { t } = useTranslation();

  const addAnswerOption = (): void => {
    const newAnswer: QuestionConfigAnswerOptionDto = {
      id: generateId(),
      translations: emptyAnswerOptionTranslations,
    };

    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions: questionConfig.answerOptions
          ? [...questionConfig.answerOptions, newAnswer]
          : [newAnswer],
      });
    }
  };

  const handleOptionChange = (index: number, value: string): void => {
    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions:
          questionConfig.answerOptions?.map((answerOption, i) =>
            index === i
              ? {
                  ...answerOption,
                  translations: answerOption.translations.map((translation) =>
                    translation.language === language
                      ? { ...translation, name: value }
                      : translation,
                  ),
                }
              : answerOption,
          ) || [],
      });
    }
  };

  const handleOptionRemove = (index: number): void => {
    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions: questionConfig.answerOptions?.filter((_, i) => index !== i),
      });
    }
  };

  return questionConfig ? (
    <Stack>
      {questionConfig.answerOptions?.map((answerOption, index) => {
        const optionTranslation = answerOption.translations.find(
          (translation) => translation.language === language,
        );

        return (
          <Stack direction="row">
            <Checkbox checked={false} />
            <TextField
              value={optionTranslation?.name || ""}
              placeholder={t("questionEditor.enterAnswer")}
              variant="standard"
              fullWidth
              onChange={(e) => handleOptionChange(index, e.target.value)}
            />
            <IconButton onClick={() => handleOptionRemove(index)}>
              <FeatherIcon icon="minus-circle" size={20} />
            </IconButton>
          </Stack>
        );
      })}
      <Button variant="outlined" onClick={addAnswerOption}>
        {t("questionEditor.addAnswer")}
      </Button>
    </Stack>
  ) : null;
};

export default CheckboxInput;
